import React, { useState, useMemo } from "react";
import { programs, programCategories } from "./data/programs";
import { processEligibility } from "./lib/eligibility";
import { getCollegesForProgram } from "./data/colleges";
import { getCollegeImagePath, getCollegeGradient } from "./data/universityImages";
import ProgramCard from "./ProgramCard";
import CollegeModal from "./CollegeModal";
import CollegeDetailModal from "./CollegeDetailModal";

const ResultsDashboard = ({ selectedSubjects, onRemoveSubject, onBack }) => {
    const [activeTab, setActiveTab] = useState("eligible");
    const [categoryFilter, setCategoryFilter] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [modalProgram, setModalProgram] = useState(null);
    const [campusFilter, setCampusFilter] = useState([]);
    const [collegeSearch, setCollegeSearch] = useState("");
    const [sortBy, setSortBy] = useState("programs");
    // New: college detail modal state
    const [detailCollege, setDetailCollege] = useState(null);

    const { eligiblePrograms, otherPrograms } = useMemo(
        () => processEligibility(programs, selectedSubjects),
        [selectedSubjects]
    );

    const eligibleColleges = useMemo(() => {
        const collegeMap = new Map();
        
        // Filter eligible programs based on selected category before calculating colleges
        const programsToConsider = categoryFilter.length > 0
            ? eligiblePrograms.filter((p) => categoryFilter.includes(p.category))
            : eligiblePrograms;

        programsToConsider.forEach((program) => {
            const colleges = getCollegesForProgram(program.name);
            colleges.forEach((college) => {
                if (!collegeMap.has(college.name)) {
                    collegeMap.set(college.name, {
                        name: college.name,
                        campus: college.campus,
                        programCount: 0,
                        programs: [],
                    });
                }
                const entry = collegeMap.get(college.name);
                entry.programCount += 1;
                if (!entry.programs.includes(program.name)) {
                    entry.programs.push(program.name);
                }
            });
        });
        return Array.from(collegeMap.values()).sort(
            (a, b) => b.programCount - a.programCount
        );
    }, [eligiblePrograms, categoryFilter]);

    // Get all unique campuses
    const allCampuses = useMemo(() => {
        const campuses = new Set();
        eligibleColleges.forEach((c) => campuses.add(c.campus || "Delhi University"));
        return Array.from(campuses);
    }, [eligibleColleges]);

    // Filter colleges
    const filteredColleges = useMemo(() => {
        let result = eligibleColleges.filter((c) => {
            const matchesCampus =
                campusFilter.length === 0 ||
                campusFilter.includes(c.campus || "Delhi University");
            const matchesSearch =
                !collegeSearch ||
                c.name.toLowerCase().includes(collegeSearch.toLowerCase());
            return matchesCampus && matchesSearch;
        });

        if (sortBy === "programs") {
            result.sort((a, b) => b.programCount - a.programCount);
        } else if (sortBy === "name") {
            result.sort((a, b) => a.name.localeCompare(b.name));
        }

        return result;
    }, [eligibleColleges, campusFilter, collegeSearch, sortBy]);

    const filterPrograms = (programList) => {
        return programList.filter((p) => {
            const matchesCategory =
                categoryFilter.length === 0 || categoryFilter.includes(p.category);
            const matchesSearch =
                !searchQuery ||
                p.name.toLowerCase().includes(searchQuery.toLowerCase());
            return matchesCategory && matchesSearch;
        });
    };

    const filteredEligible = filterPrograms(eligiblePrograms);
    const filteredOther = filterPrograms(otherPrograms);

    const handleCampusToggle = (campus) => {
        setCampusFilter((prev) =>
            prev.includes(campus)
                ? prev.filter((c) => c !== campus)
                : [...prev, campus]
        );
    };

    const handleCategoryToggle = (category) => {
        setCategoryFilter((prev) =>
            prev.includes(category)
                ? prev.filter((c) => c !== category)
                : [...prev, category]
        );
    };

    const clearAllFilters = () => {
        setCampusFilter([]);
        setCollegeSearch("");
        setCategoryFilter([]);
        setSearchQuery("");
    };

    const getMatchPercentage = (college) => {
        const maxPrograms = eligibleColleges.length > 0 ? eligibleColleges[0].programCount : 1;
        return Math.round((college.programCount / maxPrograms) * 100);
    };

    // Sidebar component — reused across all tabs
    const renderSidebar = () => (
        <aside className="elig-sidebar">
            <div className="elig-sidebar__header">
                <div className="elig-sidebar__header-left">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="14" y2="12" /><line x1="4" y1="18" x2="10" y2="18" /></svg>
                    <span className="elig-sidebar__title">Filters</span>
                </div>
                <button className="elig-sidebar__clear" onClick={clearAllFilters}>Clear All</button>
            </div>

            {/* Campus filter */}
            <div className="elig-sidebar__section">
                <h4 className="elig-sidebar__section-title">CAMPUS</h4>
                {allCampuses.map((campus) => (
                    <label key={campus} className="elig-checkbox-row">
                        <input
                            type="checkbox"
                            checked={campusFilter.includes(campus)}
                            onChange={() => handleCampusToggle(campus)}
                            className="elig-checkbox"
                        />
                        <span>{campus}</span>
                    </label>
                ))}
            </div>

            {/* Category filter — shown universally */}
            <div className="elig-sidebar__section">
                <h4 className="elig-sidebar__section-title">CATEGORY</h4>
                {programCategories.map((cat) => (
                    <label key={cat} className="elig-checkbox-row">
                        <input
                            type="checkbox"
                            checked={categoryFilter.includes(cat)}
                            onChange={() => handleCategoryToggle(cat)}
                            className="elig-checkbox"
                        />
                        <span>{cat}</span>
                    </label>
                ))}
            </div>

            {/* Search */}
            <div className="elig-sidebar__section">
                <h4 className="elig-sidebar__section-title">
                    {activeTab === "colleges" ? "SEARCH COLLEGE" : "SEARCH PROGRAM"}
                </h4>
                <div className="elig-sidebar__search">
                    <span className="elig-sidebar__search-icon">🔍</span>
                    <input
                        type="text"
                        placeholder={activeTab === "colleges" ? "Search college name" : "Search program name"}
                        value={activeTab === "colleges" ? collegeSearch : searchQuery}
                        onChange={(e) => {
                            if (activeTab === "colleges") {
                                setCollegeSearch(e.target.value);
                            } else {
                                setSearchQuery(e.target.value);
                            }
                        }}
                    />
                </div>
            </div>

            {/* Sort — colleges tab only */}
            {activeTab === "colleges" && (
                <div className="elig-sidebar__section">
                    <h4 className="elig-sidebar__section-title">SORT BY</h4>
                    <select
                        className="elig-sidebar__select"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                    >
                        <option value="programs">Most Programs</option>
                        <option value="name">Name (A-Z)</option>
                    </select>
                </div>
            )}

            <div className="elig-sidebar__help">
                <span className="elig-sidebar__help-icon">💡</span>
                <h4>Need Help?</h4>
                <p>Our counseling experts can help you choose the best college based on your CUET scores.</p>
                <a 
                    href="https://wa.me/919810033495" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="elig-sidebar__help-btn"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                    </svg>
                    Talk to Expert
                </a>
            </div>
        </aside>
    );

    return (
        <div className="elig-results-section">
            {/* Breadcrumb */}
            <div className="elig-breadcrumb">
                <button className="elig-breadcrumb__link" onClick={onBack}>Home</button>
                <span className="elig-breadcrumb__sep">›</span>
                <span className="elig-breadcrumb__current">Eligible Colleges</span>
            </div>

            {/* Results Header */}
            <div className="elig-results-top">
                <div>
                    <h2 className="elig-results-top__title">
                        Showing{" "}
                        {activeTab === "colleges"
                            ? `${filteredColleges.length} Colleges`
                            : activeTab === "eligible"
                                ? `${filteredEligible.length} Eligible Programs`
                                : `${filteredOther.length} Other Programs`}
                    </h2>
                    <div className="elig-results-top__subjects-row">
                        <span className="elig-results-top__subjects-label">Found based on your selected subjects:</span>
                        <div className="elig-results-top__subjects-chips">
                            {selectedSubjects.map((subject) => (
                                <span key={subject} className="elig-results-top__chip">
                                    {subject}
                                    <button
                                        className="elig-results-top__chip-remove"
                                        onClick={() => onRemoveSubject(subject)}
                                        title={`Remove ${subject}`}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="elig-nav-tabs">
                <button
                    className={`elig-nav-tab ${activeTab === "colleges" ? "elig-nav-tab--active" : ""}`}
                    onClick={() => setActiveTab("colleges")}
                >
                    🏛️ Colleges ({filteredColleges.length})
                </button>
                <button
                    className={`elig-nav-tab ${activeTab === "eligible" ? "elig-nav-tab--active" : ""}`}
                    onClick={() => setActiveTab("eligible")}
                >
                    ✅ Eligible Programs ({filteredEligible.length})
                </button>
                <button
                    className={`elig-nav-tab ${activeTab === "other" ? "elig-nav-tab--active" : ""}`}
                    onClick={() => setActiveTab("other")}
                >
                    📋 Other Programs ({filteredOther.length})
                </button>
            </div>

            {/* Unified layout — sidebar + content for ALL tabs */}
            <div className="elig-results-layout">
                {renderSidebar()}

                <div className="elig-cards-area">
                    {/* College Tab */}

                    {/* Eligible Programs Tab */}
                    {activeTab === "eligible" && (
                        <>
                            {filteredEligible.length === 0 ? (
                                <div className="elig-empty-state">
                                    <div className="elig-empty-state__icon">🔍</div>
                                    <div className="elig-empty-state__text">No eligible programs found for this filter.</div>
                                </div>
                            ) : (
                                <div className="elig-programs-grid">
                                    {filteredEligible.map((program, i) => (
                                        <ProgramCard key={program.id} program={program} showMissing={false} onViewColleges={setModalProgram} index={i} />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                    {activeTab === "colleges" && (
                        <>
                            {filteredColleges.length === 0 ? (
                                <div className="elig-empty-state">
                                    <div className="elig-empty-state__icon">🏛️</div>
                                    <div className="elig-empty-state__text">No colleges found matching your filters.</div>
                                </div>
                            ) : (
                                <div className="elig-college-grid">
                                    {filteredColleges.map((college, idx) => {
                                        const imagePath = getCollegeImagePath(college.name);
                                        const gradient = getCollegeGradient(college.name);
                                        const matchPct = getMatchPercentage(college);

                                        return (
                                            <div className="elig-college-card" key={idx}>
                                                <div
                                                    className="elig-college-card__banner"
                                                    style={{
                                                        background: imagePath
                                                            ? `linear-gradient(135deg, ${gradient[0]}dd, ${gradient[1]}aa), url(${imagePath}) center/cover`
                                                            : `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
                                                    }}
                                                >
                                                    <div className="elig-college-card__badge">
                                                        {college.programCount} {college.programCount === 1 ? "Program" : "Programs"}
                                                    </div>
                                                </div>
                                                <div className="elig-college-card__body">
                                                    <h3 className="elig-college-card__name">{college.name}</h3>
                                                    <div className="elig-college-card__location">
                                                        📍 {college.campus || "Delhi University"}
                                                    </div>
                                                    <div className="elig-college-card__match">
                                                        <span className="elig-college-card__match-label">Eligibility Match:</span>
                                                        <span className={`elig-college-card__match-value ${matchPct >= 80 ? "elig-college-card__match-value--high" : ""}`}>
                                                            {matchPct}% Match
                                                        </span>
                                                    </div>
                                                    <div className="elig-college-card__match-bar">
                                                        <div className="elig-college-card__match-fill" style={{ width: `${matchPct}%` }} />
                                                    </div>
                                                    <button
                                                        className="elig-college-card__action"
                                                        onClick={() => setDetailCollege(college)}
                                                    >
                                                        View All Programs & Cutoffs <span>→</span>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}


                    {/* Other Programs Tab */}
                    {activeTab === "other" && (
                        <>
                            {filteredOther.length === 0 ? (
                                <div className="elig-empty-state">
                                    <div className="elig-empty-state__icon">✅</div>
                                    <div className="elig-empty-state__text">You're eligible for all programs in this category!</div>
                                </div>
                            ) : (
                                <div className="elig-programs-grid">
                                    {filteredOther.map((program, i) => (
                                        <ProgramCard key={program.id} program={program} showMissing={true} onViewColleges={setModalProgram} index={i} />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Program-specific college modal (from Eligible/Other Programs tab) */}
            {modalProgram && (
                <CollegeModal program={modalProgram} onClose={() => setModalProgram(null)} />
            )}

            {/* College detail modal (from Colleges tab — shows all programs for that college) */}
            {detailCollege && (
                <CollegeDetailModal
                    collegeName={detailCollege.name}
                    campus={detailCollege.campus}
                    onClose={() => setDetailCollege(null)}
                />
            )}
        </div>
    );
};

export default ResultsDashboard;
